import React, { useState } from 'react';
import Navbar from "../navbar/Navbar";
import Footer from "../../footer/Footer";
import FAQIMG from "../../../Assets/FAQIM.jpeg"; 
import { Helmet } from 'react-helmet-async';

interface FAQItem {
  question: string;
  answer: string;
}

interface ActiveIndices {
  general: number | null;
  petOwner: number | null;
  community: number | null;
}

const FAQPage: React.FC = () => {
  const [activeIndices, setActiveIndices] = useState<ActiveIndices>({
    general: null,
    petOwner: null,
    community: null
  });

  const toggleAnswer = (section: keyof ActiveIndices, index: number): void => {
    setActiveIndices(prev => ({
      ...prev,
      [section]: prev[section] === index ? null : index
    }));
  };

  const faqData1: FAQItem[] = [ // General FAQ
    {
      question: "What is PupHub?",
      answer: "PupHub is a free social community app for pet lovers where you can share pet stories and blogs, read pet-related news, connect with other pet parents, and discover nearby services like vets, groomers, and pet pharmacies using your area PIN."
    },
    {
      question: "Is PupHub free to use?",
      answer: "Yes, PupHub is completely free for all users."
    },
    {
      question: "How do I contact PupHub support?",
      answer: "You can contact our support team by phone at +918918080399 or email us at apexiumtechnologies@gmail.com. Visit our Contact page for more details."
    }
  ];

  const faqData2: FAQItem[] = [ // Pet Owner FAQ
    {
      question: "How do I join the PupHub community?",
      answer: "Download the PupHub app, sign up with your details, and start exploring the community, sharing pet stories, and connecting with other users."
    },
    {
      question: "Can I post about my pet on PupHub?",
      answer: "Yes! You can post pet stories, images, blogs, and updates to share with other community members."
    },
    {
      question: "How do I search for services like vets or groomers?",
      answer: "Use the search feature and enter your area PIN to discover nearby clinics, grooming centers, pharmacies, and more."
    },
    {
      question: "Is there a way to chat with other pet parents?",
      answer: "Yes, PupHub allows you to chat and connect with other users to share experiences, tips, and pet advice."
    }
  ];

  const faqData3: FAQItem[] = [ // Community Feature FAQ
    {
      question: "What kind of content can I post on PupHub?",
      answer: "You can post pet blogs, stories, photos, and even tips or experiences related to pet care."
    },
    {
      question: "How do I keep my profile updated?",
      answer: "You can edit your profile anytime to update your pet details, profile photo, and preferences."
    }
  ];

  const renderFAQSection = (section: keyof ActiveIndices, data: FAQItem[], title: string) => (
    <div className="mb-10 space-y-4">
      <h2 className="text-center sm:text-justify text-2xl max-w-4xl text-white mt-10 m-5">
        {title}
      </h2>
      {data.map((faq, index) => (
        <div
          key={`${section}-${index}`}
          className="bg-white text-black rounded-lg shadow-md"
        >
          <button
            onClick={() => toggleAnswer(section, index)}
            className="flex justify-between items-center w-full p-4 text-lg font-semibold focus:outline-none"
            aria-expanded={activeIndices[section] === index}
            aria-controls={`${section}-answer-${index}`}
          >
            {faq.question}
            <span className="text-gray-500">
              {activeIndices[section] === index ? "-" : "+"}
            </span>
          </button>
          {activeIndices[section] === index && (
            <div 
              id={`${section}-answer-${index}`}
              className="px-4 pb-4 text-gray-700"
              role="region"
            >
              {faq.answer}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://puphub.co.in/FAQ" />
        <title>PupHub | FAQ</title>
        <meta
          name="description"
          content="Welcome to PupHub, your pet-friendly companion app."
        />
      </Helmet>
      
      <Navbar />
      
      <div
        className="w-[100vw] h-[40vh] sm:h-[70vh] relative"
        style={{
          backgroundImage: `url("https://puphub-customer.s3.ap-south-1.amazonaws.com/static_data/images/FAQIM.jpeg")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
        role="img"
        aria-label="Frequently asked questions about PupHub"
      />

      <div className="flex justify-center">
        <p className="text-center sm:text-justify text-2xl max-w-4xl text-white mt-10 m-5">
          Welcome to our FAQ page! Here, we answer all your questions about dog
          care, boarding, training, and more. Whether you're a new dog owner or
          looking for expert tips, browse through our frequently asked questions
          to find the information you need to keep your pet happy, healthy, and
          safe.
        </p>
      </div>

      <div className="p-8 max-w-4xl mx-auto space-y-4">
        {renderFAQSection("general", faqData1, "General FAQs")}
        {renderFAQSection("petOwner", faqData2, "Pet Owner FAQs")}
        {renderFAQSection("community", faqData3, "Community Features")}
      </div>
      
      <Footer />
    </>
  );
};

export default FAQPage;